import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Pages.css';

const emptyForm = { code: '', description: '', discount_type: 'percent', discount_value: '', service_id: '', max_uses: '', expires_at: '' };

function AdminCoupons({ apiUrl, token }) {
  const [coupons, setCoupons] = useState([]);
  const [services, setServices] = useState([]);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = () => {
    axios.get(`${apiUrl}/admin/coupons`, { headers }).then((res) => setCoupons(res.data)).catch((err) => setError(err.response?.data?.error || 'Failed to load coupons'));
    axios.get(`${apiUrl}/admin/services`, { headers }).then((res) => setServices(res.data)).catch(() => {});
  };

  useEffect(fetchAll, [apiUrl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setFormData({
      code: c.code,
      description: c.description || '',
      discount_type: c.discount_type,
      discount_value: c.discount_value,
      service_id: c.service_id || '',
      max_uses: c.max_uses || '',
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      ...formData,
      service_id: formData.service_id || null,
      max_uses: formData.max_uses || null,
      expires_at: formData.expires_at || null
    };
    try {
      if (editingId) {
        await axios.patch(`${apiUrl}/admin/coupons/${editingId}`, payload, { headers });
      } else {
        await axios.post(`${apiUrl}/admin/coupons`, payload, { headers });
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save coupon');
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await axios.delete(`${apiUrl}/admin/coupons/${id}`, { headers });
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete coupon');
    }
  };

  const toggleActive = async (c) => {
    setError('');
    try {
      await axios.patch(`${apiUrl}/admin/coupons/${c.id}`, { active: !c.active }, { headers });
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update coupon');
    }
  };

  const discountLabel = (c) => (c.discount_type === 'percent' ? `${parseFloat(c.discount_value)}% off` : `$${parseFloat(c.discount_value).toFixed(2)} off`);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Manage Coupons</h1>
        <button className="btn btn-primary" onClick={openCreate}>+ New Coupon</button>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="list-container table-wrap">
        <table className="table">
          <thead>
            <tr><th>Code</th><th>Discount</th><th>Applies To</th><th>Uses</th><th>Expires</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.code}</strong>{c.description && <div className="text-muted" style={{ fontSize: '0.8rem' }}>{c.description}</div>}</td>
                <td>{discountLabel(c)}</td>
                <td>{c.service_name || <span className="text-muted">Any service</span>}</td>
                <td>{c.times_used}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                <td>{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : <span className="text-muted">Never</span>}</td>
                <td>
                  <span className={`badge badge-${c.active ? 'active' : 'inactive'}`} style={{ cursor: 'pointer' }} onClick={() => toggleActive(c)}>
                    {c.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)} style={{ marginRight: 6 }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {coupons.length === 0 && <tr><td colSpan="7" className="empty-state">No coupons yet</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Coupon' : 'New Coupon'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Code</label>
                <input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. WELCOME10"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description (internal note)</label>
                <input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Discount Type</label>
                  <select value={formData.discount_type} onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}>
                    <option value="percent">Percent off</option>
                    <option value="fixed">Fixed amount off</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{formData.discount_type === 'percent' ? 'Percent (%)' : 'Amount ($)'}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Applies To</label>
                <select value={formData.service_id} onChange={(e) => setFormData({ ...formData, service_id: e.target.value })}>
                  <option value="">Any service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.practitioner_name})</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Max Uses (optional)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    placeholder="Unlimited"
                  />
                </div>
                <div className="form-group">
                  <label>Expires On (optional)</label>
                  <input type="date" value={formData.expires_at} onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-block">{editingId ? 'Save Changes' : 'Create Coupon'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCoupons;
